import { z } from 'zod'

export const bookingStatusSchema = z.enum([
  'NEEDS_SELECTION',
  'NEEDS_DETAILS',
  'NEEDS_CONFIRMATION',
  'READY_TO_CONFIRM',
  'SUBMITTING',
  'CREATE_UNKNOWN',
  'ORDER_CREATED',
  'FAILED',
  'EXPIRED',
])
const money = z.object({
  currency: z.literal('CNY'),
  amount_minor: z.number().int().positive().safe(),
})
export const hotelGuestFormSchema = z
  .object({
    room_count: z.number().int().min(1).max(20),
    guest_names: z.array(z.string().trim().min(1).max(64)),
    contact_name: z.string().trim().min(1).max(64),
    contact_mobile: z
      .string()
      .trim()
      .regex(/^\+?[0-9][0-9 -]{4,30}$/),
    contact_email: z.union([z.literal(''), z.email().max(254)]),
    arrive_time: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)]),
    remark: z.string().max(500),
    occupancy_acknowledged: z.boolean(),
  })
  .refine((f) => f.guest_names.length === f.room_count, { message: '每间房须填写一位登记人' })
export type HotelGuestForm = z.infer<typeof hotelGuestFormSchema>
export const bookingSourceRequestSchema = z.object({
  planner_id: z.string().min(1).max(64),
  version_id: z.string().min(1).max(64),
  module_id: z.string().min(1).max(64),
  expected_revision: z.number().int().nonnegative(),
  request_key: z.string().min(1).max(128),
})
export type BookingSourceRequest = z.infer<typeof bookingSourceRequestSchema>
export const hotelBookingSessionSchema = z.object({
  schema_version: z.literal('booking_session_v1'),
  booking_session_id: z.string().min(1),
  session_revision: z.number().int().nonnegative(),
  status: bookingStatusSchema,
  order_no: z.string().nullable(),
  domain: z.literal('HOTEL'),
  source: z.object({
    planner_id: z.string(),
    version_id: z.string(),
    module_id: z.string(),
    revision: z.number().int(),
  }),
  source_snapshot: z.object({
    domain: z.literal('HOTEL'),
    travelers: z.object({
      adults: z.number().int().nonnegative(),
      children: z.number().int().nonnegative(),
      infants: z.number().int().nonnegative(),
    }),
    city: z.object({ name: z.string(), timezone: z.string().nullable() }),
    check_in_date: z.string(),
    check_out_date: z.string(),
    room_count: z.number().int().nullable(),
  }),
  created_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
})
export type HotelBookingSession = z.infer<typeof hotelBookingSessionSchema>
export const hotelCheckoutSchema = z
  .object({
    schema_version: z.literal('hotel_checkout_v1'),
    booking_session_id: z.string().min(1),
    session_revision: z.number().int().nonnegative(),
    status: bookingStatusSchema,
    order_no: z.string().nullable(),
    hotel_name: z.string(),
    hotel_phone: z.string(),
    children_policy_text: z.string().nullable(),
    form: hotelGuestFormSchema.nullable(),
    confirmation_id: z.string().nullable(),
    quote_valid_until: z.iso.datetime().nullable(),
    quote_expired: z.boolean(),
    confirmation_valid_until: z.iso.datetime().nullable(),
    confirmation_total: money.nullable(),
    candidates: z.array(
      z.object({
        candidate_ref: z.string().min(1),
        room_name: z.string(),
        bed_type: z.string(),
        capacity_text: z.string(),
        rate_name: z.string(),
        breakfast: z.string(),
        instant_confirm: z.boolean(),
        cancellation_text: z.string(),
        cancel_policy_type: z.number().int(),
        free_cancel_deadline: z.string(),
        selected: z.boolean(),
        occupancy_acknowledgment_required: z.boolean(),
        per_room_stay_total: money,
        total: money.nullable(),
        amount_supported: z.boolean(),
      }),
    ),
  })
  .refine((c) => c.status !== 'ORDER_CREATED' || !!c.order_no, { message: '已建单必须包含订单号' })
export type HotelCheckout = z.infer<typeof hotelCheckoutSchema>
